import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useOnboarding } from './OnboardingContext';
import { ASSETS } from './assets';

/**
 * CompleteScreen — shown right after the last onboarding step.
 * Plays a warm chime and lingers 2.6s on: "Your first step towards your goal is complete."
 * Then advances to the cinematic opening.
 */
export default function CompleteScreen() {
    const { setPhase } = useOnboarding();
    const audioRef = useRef(null);

    useEffect(() => {
        // Play the completion chime (best-effort; ignore autoplay rejection)
        try {
            const a = new Audio(ASSETS.chime);
            a.volume = 0.85;
            audioRef.current = a;
            a.play().catch(() => {});
        } catch {}

        const t = setTimeout(() => setPhase('opening'), 2700);
        return () => {
            clearTimeout(t);
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
    }, [setPhase]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="relative w-full flex items-center justify-center px-6 z-50"
            data-testid="complete-screen"
        >
            <div className="max-w-2xl text-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, ease: [0.83, 0, 0.17, 1] }}
                    className="mb-6 mx-auto h-14 w-14 rounded-full flex items-center justify-center"
                    style={{
                        background: 'var(--lavender)',
                        boxShadow: '0 0 40px rgba(183,156,255,0.7)',
                    }}
                >
                    <motion.svg
                        width="26" height="26" viewBox="0 0 24 24" fill="none"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                    >
                        <motion.path
                            d="M4 12.5l5 5L20 6.5"
                            stroke="#2A3115"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        />
                    </motion.svg>
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.4, ease: [0.83, 0, 0.17, 1] }}
                    className="display text-[color:var(--parchment)]"
                    style={{ fontSize: 'clamp(30px, 4.6vw, 60px)', lineHeight: 1.06 }}
                >
                    Your first step towards
                    <br />
                    your goal is{' '}
                    <span className="display-italic" style={{ color: 'var(--lavender)' }}>
                        complete.
                    </span>
                </motion.h1>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4 }}
                    className="mt-8 flex items-center justify-center gap-1.5"
                >
                    {[0, 1, 2].map((i) => (
                        <motion.div
                            key={i}
                            className="h-1.5 w-1.5 rounded-full bg-[color:var(--lavender)]"
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.2 }}
                        />
                    ))}
                </motion.div>
            </div>
        </motion.div>
    );
}
